import { Box, Stack, Container, Typography } from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { Iconify, APP_ICONS } from 'src/components/iconify';

const FEATURES = [
  {
    icon: APP_ICONS.communication.chatDots,
    text: 'Kết nối nhiều tài khoản zalo cá nhân',
    bgcolor: '#FFA51F',
    glow: 'rgba(229, 62, 136, 0.58)',
  },
  {
    icon: APP_ICONS.schedule.calendar,
    text: 'Lưu lịch sử hội thoại theo khách hàng',
    bgcolor: '#6C63FF',
    glow: 'rgba(164, 74, 211, 0.48)',
  },
  {
    icon: APP_ICONS.communication.bell,
    text: 'Tự động hóa nhắc việc & CSKH',
    bgcolor: '#FF5C3B',
    glow: 'rgba(236, 145, 55, 0.55)',
  },
];

export const ZaloFeature = () => {
  const smDown = useResponsive('down', 'sm');

  return (
    <Stack
      component="section"
      px={{ xs: 2, md: 4 }}
      py={{ xs: 7, md: 10 }}
      sx={{
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',

        color: 'common.white',
        bgcolor: '#050505',

        // Ánh sáng trắng góc trên bên phải
        '&::before': {
          content: '""',
          position: 'absolute',
          zIndex: 0,
          pointerEvents: 'none',

          top: -180,
          right: -120,
          width: 520,
          height: 420,

          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.045) 40%, transparent 70%)',
          filter: 'blur(35px)',
        },

        // Lớp tối viền ngoài
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',

          background: `
            radial-gradient(
              ellipse at center,
              transparent 35%,
              rgba(0, 0, 0, 0.12) 68%,
              rgba(0, 0, 0, 0.5) 100%
            )
          `,
        },
      }}
    >
      {/* Glow hồng bên trái, nằm dưới các feature card */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          zIndex: 0,
          pointerEvents: 'none',

          top: { xs: 210, md: 250 },
          left: { xs: -160, md: '5%' },

          width: 420,
          height: 210,
          borderRadius: '50%',

          bgcolor: 'rgba(194, 38, 123, 0.38)',
          filter: 'blur(95px)',
          opacity: 0.85,
          mixBlendMode: 'screen',
        }}
      />

      {/* Glow cam bên phải, nằm dưới card thứ ba */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          zIndex: 0,
          pointerEvents: 'none',

          top: { xs: 250, md: 270 },
          right: { xs: -180, md: '4%' },

          width: 400,
          height: 240,
          borderRadius: '50%',

          bgcolor: 'rgba(221, 128, 47, 0.34)',
          filter: 'blur(100px)',
          opacity: 0.9,
          mixBlendMode: 'screen',
        }}
      />

      {/* Glow xanh cyan góc dưới trái */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          zIndex: 0,
          pointerEvents: 'none',

          left: { xs: -210, md: '-4%' },
          bottom: { xs: -130, md: -90 },

          width: 520,
          height: 320,
          borderRadius: '50%',

          bgcolor: 'rgba(34, 177, 230, 0.3)',
          filter: 'blur(115px)',
          opacity: 0.8,
          mixBlendMode: 'screen',
        }}
      />

      {/* Glow xanh dương góc dưới phải */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          zIndex: 0,
          pointerEvents: 'none',

          right: { xs: -240, md: '-6%' },
          bottom: { xs: -120, md: -100 },

          width: 560,
          height: 340,
          borderRadius: '50%',

          bgcolor: 'rgba(25, 71, 220, 0.28)',
          filter: 'blur(120px)',
          opacity: 0.85,
          mixBlendMode: 'screen',
        }}
      />

      {/* Các chấm sáng mờ giống ảnh mẫu */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          zIndex: 0,
          pointerEvents: 'none',

          top: '7%',
          left: '48%',

          width: 17,
          height: 17,
          borderRadius: '50%',

          bgcolor: 'rgba(255,255,255,0.06)',
          filter: 'blur(4px)',
        }}
      />

      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          zIndex: 0,
          pointerEvents: 'none',

          top: '34%',
          right: '5%',

          width: 18,
          height: 18,
          borderRadius: '50%',

          bgcolor: 'rgba(255,255,255,0.055)',
          filter: 'blur(5px)',
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Stack spacing={{ xs: 6, md: 9 }}>
          <Typography align="center" variant="h2" fontWeight="bold">
            Tích hợp Zalo cá nhân
          </Typography>

          <Stack spacing={{ xs: 3, md: 4 }}>
            <Stack direction="row" alignItems="stretch" spacing={{ xs: 1, md: 1.5 }}>
              {FEATURES.map((item) => (
                <Box
                  key={item.text}
                  sx={{
                    position: 'relative',
                    isolation: 'isolate',
                    overflow: 'hidden',

                    flex: 1,
                    minWidth: 0,
                    minHeight: { xs: 40, md: 136 },
                    p: { xs: 1.2, sm: 2, md: 3.5 },

                    color: 'common.white',
                    borderRadius: { xs: 0.8, sm: 1.5, md: 3 },

                    background: `
                        linear-gradient(
                          145deg,
                          rgba(255, 255, 255, 0.065) 0%,
                          rgba(255, 255, 255, 0.018) 42%,
                          rgba(0, 0, 0, 0.14) 100%
                        ),
                        rgba(8, 8, 10, 0.62)
                      `,

                    backdropFilter: 'blur(20px) saturate(145%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(145%)',

                    boxShadow: `
                        0 18px 42px rgba(0, 0, 0, 0.38),
                        inset 0 1px 0 rgba(255, 255, 255, 0.11),
                        inset 0 -1px 0 rgba(255, 255, 255, 0.04)
                      `,

                    // Viền kính
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      zIndex: -1,

                      padding: '1px',
                      borderRadius: 'inherit',

                      background: `
                          linear-gradient(
                            145deg,
                            rgba(255, 255, 255, 0.4) 0%,
                            rgba(255, 255, 255, 0.12) 30%,
                            rgba(255, 255, 255, 0.025) 60%,
                            rgba(255, 255, 255, 0.18) 100%
                          )
                        `,

                      WebkitMask:
                        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',

                      pointerEvents: 'none',
                    },

                    // Mỗi card có một màu glow riêng
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      zIndex: -1,
                      pointerEvents: 'none',

                      width: 240,
                      height: 150,
                      right: -55,
                      bottom: -95,

                      borderRadius: '50%',
                      background: `radial-gradient(
                          ellipse,
                          ${item.glow} 0%,
                          transparent 70%
                        )`,

                      filter: 'blur(18px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,

                      width: { xs: 16, sm: 28, md: 38 },
                      height: { xs: 16, sm: 28, md: 38 },
                      mb: { xs: 1, md: 1.5 },

                      borderRadius: { xs: 0.5, sm: 1, md: 1.5 },
                      bgcolor: item.bgcolor,

                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',

                      boxShadow: `0 8px 22px ${item.glow}`,
                    }}
                  >
                    <Iconify icon={item.icon} width={smDown ? 12 : 20} />
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      fontSize: { xs: 5.2, sm: 14, md: 16 },
                      fontWeight: 400,
                      color: 'rgba(255,255,255,0.94)',
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* Wrapper tạo ánh sáng xung quanh ảnh */}
            <Box
              sx={{
                position: 'relative',

                '&::before': {
                  content: '""',
                  position: 'absolute',
                  zIndex: 0,
                  pointerEvents: 'none',

                  left: '4%',
                  right: '4%',
                  bottom: -30,
                  height: 90,

                  borderRadius: '50%',
                  background: `
                    linear-gradient(
                      90deg,
                      rgba(30, 190, 227, 0.32),
                      rgba(37, 82, 224, 0.4)
                    )
                  `,
                  filter: 'blur(45px)',
                },

                '&::after': {
                  content: '""',
                  position: 'absolute',
                  zIndex: 0,
                  pointerEvents: 'none',

                  inset: '8% 3% 1%',
                  borderRadius: '28px',

                  boxShadow: `
                    0 25px 70px rgba(29, 93, 255, 0.18),
                    -25px 20px 70px rgba(20, 190, 224, 0.12)
                  `,
                },
              }}
            >
              <Box
                component="img"
                src="/assets/landing/zalo/zalo-feature.png"
                alt="Tích hợp Zalo cá nhân"
                sx={{
                  position: 'relative',
                  zIndex: 1,

                  display: 'block',
                  width: '100%',
                  height: 'auto',

                  filter: `
                    drop-shadow(0 18px 35px rgba(0, 0, 0, 0.55))
                    drop-shadow(0 10px 24px rgba(30, 86, 255, 0.12))
                  `,
                }}
              />
            </Box>
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
};
